import React, { useState, useEffect, useRef } from 'react';
import { Container, Modal } from 'react-bootstrap';
import styleClasses from '../try2.module.scss';
import 'swiper/swiper.scss';
import 'swiper/modules/navigation/navigation.scss';
import {
  IModifiersObjects,
  ITry2Customization,
} from '../../../models/try2Combo';
import ApplyButton from './try2Customization/ApplyButton';
import { useSelector, useDispatch } from 'react-redux';
import {
  removeTemporarilyAddedPriceForItem,
  updateTry2ComboSelectedModifiers,
} from '../../../redux/slices/itemCustomizationSlice';
import AddIngredients from './try2Customization/AddIngredients';
import { itemBuckets } from '../../../priceCalculation/buckets';
import {
  ADD_ONS,
  CORE_MODIFIERS,
    REACT_APP_SOUP_CATEGORY_ID_FOR_TRY2COMBO_ADDONS_UPSELL,
  REQUIRED_MODIFIERS,
  TRY_TO_COMBO_ITEM,
} from '../../../constants';
import { modifiersAgainstAnItem } from '../../../priceCalculation/helper';

const NOT_SOUP = "Not Soup";

const Try2CustomizationModal: React.FunctionComponent<ITry2Customization> = ({
  showCustomizationModal,
  closeCustomizationModal,
  modifierGroups,
  handleIngredientSelection,
  coreModifiers,
  requiredModifiers,
  addItemtoComboClick,
  handleSyncIngredients,
  selectedItem,
  sectionSelected,
  removeSelectedIngredients,
  currentEditItem,
  noDressingHandler,
  itemName,
  isCreateYourOwnItem,
  isItEdit = false,
  showProteinModalOnCombo,
}) => {

  const openAccordionFor = useRef<typeof CORE_MODIFIERS | typeof REQUIRED_MODIFIERS | typeof ADD_ONS | typeof NOT_SOUP>(null);
  const [manualReRender, setManualRerender] = useState<boolean>(false);

  const order = useSelector((state: any) => state.itemCustomization);
  const dispatch = useDispatch();
  const [disableApply, setDisableApply] = useState<boolean>(false);
  const [selectedCustomizeData, setSelectedCustomizeData] = useState<
    IModifiersObjects[]
  >([]);

  useEffect(() => {
    if(openAccordionFor.current) return;
    if(selectedItem?.category_id != REACT_APP_SOUP_CATEGORY_ID_FOR_TRY2COMBO_ADDONS_UPSELL) openAccordionFor.current = NOT_SOUP;
    else if(coreModifiers.length > 0) openAccordionFor.current = CORE_MODIFIERS;
    else if(requiredModifiers.length > 0) openAccordionFor.current = REQUIRED_MODIFIERS;
    else if(modifierGroups.length > 0) openAccordionFor.current = ADD_ONS;
    setManualRerender(!manualReRender);
  },[])

  const addSelectedModifiers = (selectedModifier, commulativeItems): void => {
    selectedModifier?.modifiers.forEach((element) => {
      const index = commulativeItems.findIndex(
        (e) => e.groupId === element?.modifier_group_id
      );
      if (index !== -1) {
        commulativeItems[index].quantity =
          commulativeItems[index].quantity + element?.quantity;
        commulativeItems[index].noDressing = false;
      } else {
        const trackObj = {
          groupId: element?.modifier_group_id,
          quantity: element?.quantity,
          noDressing: false,
        };
        commulativeItems.push(trackObj);
      }
    });
  };

  const addNoDressings = (selectedModifier, commulativeItems): void => {
    selectedModifier?.noDressings.forEach((element) => {
      const index = commulativeItems.findIndex(
        (e) => e.groupId === element?.id
      );
      if (index === -1) {
        const trackObj = {
          groupId: element?.id,
          quantity: 0,
          noDressing: element?.noDressing,
        };
        commulativeItems.push(trackObj);
      } else {
        commulativeItems[index].noDressing = element?.noDressing;
      }
    });
  };

  const getOrderButtonState = () => {
    let activeItem = null;
    if (currentEditItem) {
      activeItem = currentEditItem;
    } else {
      currentEditItem = sectionSelected;
    }
    if (requiredModifiers) {
      const selectedModifier = itemBuckets.getSingleBucket({
        name: REQUIRED_MODIFIERS,
        fromItem: parseInt(activeItem),
      });
      const commulativeItems = [];
      if (!requiredModifiers.length) {
        setDisableApply(false);
        return;
      }
      addSelectedModifiers(selectedModifier, commulativeItems);

      if (selectedModifier?.noDressings) {
        addNoDressings(selectedModifier, commulativeItems);
      }

      let isMinExeceeded = null;
      for (let i = 0; i < requiredModifiers.length; i++) {
        const itemIndex = commulativeItems.findIndex(
          (e) => e.groupId === requiredModifiers[i].id
        );
        const minReached =
          commulativeItems[itemIndex]?.noDressing === false
            ? commulativeItems[itemIndex]?.quantity > 0 &&
              commulativeItems[itemIndex]?.quantity >= requiredModifiers[i].min
            : true;
        if (itemIndex === -1 || !minReached) {
          isMinExeceeded = false;
          break;
        } else {
          isMinExeceeded = true;
        }
      }

      if (isMinExeceeded) {
        setDisableApply(false);
      } else {
        setDisableApply(true);
      }
    } else {
      setDisableApply(true);
    }
  };

  useEffect(() => {
    getOrderButtonState();
  }, [getOrderButtonState, order]);

  const updateSelectedModifiers = (modifier: IModifiersObjects) => {
    updateSelectedCustomizeData(modifier);
    handleIngredientSelection(modifier);
  };

  const addOldQuantityToModifier = (quantity: number, index: number) => {
    const exist = selectedCustomizeData[index];
    if (index === -1 || typeof exist?.oldQuantity === 'undefined') {
      if (quantity) {
        return quantity;
      } else {
        return 0;
      }
    } else {
      return exist.oldQuantity;
    }
  };

  const updateSelectedCustomizeData = (modifier: IModifiersObjects) => {
    let quantity = null;
    if (modifier?.quantity) {
      const item = order?.modifiers.find(
        (item) => item.item === currentEditItem
      );
      const updateModifier = item?.modifiers.find(
        (currentModifier) =>
          currentModifier?.modifier_id === modifier.modifier_id
      );
      quantity = updateModifier?.quantity;
    }
    const index = selectedCustomizeData?.findIndex(
      (selectedModifier) =>
        selectedModifier.modifier_id === modifier.modifier_id
    );
    modifier.oldQuantity = addOldQuantityToModifier(quantity, index);
    if (index !== -1) {
      selectedCustomizeData[index] = modifier;
      setSelectedCustomizeData([...selectedCustomizeData]);
    } else {
      setSelectedCustomizeData([...selectedCustomizeData, modifier]);
    }
  };

  const addSelectedIngredients = () => {
    closeCustomizationModal(false);
    addItemtoComboClick(selectedItem);
  };

  const closeModal = () => {
    const itemId: any = currentEditItem ? currentEditItem : sectionSelected;
    removeSelectedIngredients(selectedCustomizeData, sectionSelected);
    closeCustomizationModal(true);
    dispatch(
      removeTemporarilyAddedPriceForItem({
        price: itemBuckets.discardBucketChanges(itemId),
        item: itemId,
      })
    );
    dispatch(
      updateTry2ComboSelectedModifiers({
        itemModifiers: modifiersAgainstAnItem({
          itemType: TRY_TO_COMBO_ITEM,
          itemId,
        }),
        itemId,
      })
    );
  };

  const isOldAndNewBucketSame =
    selectedItem && selectedItem?.category_id == 3 && showCustomizationModal
      ? itemBuckets.isItemBucketAndCopyOfItemBucketSame({
          compareCoreModifiers: selectedItem.core_modifiers.length > 0,
          compareAddOnsModifiers: selectedItem.items_modifier_groups.length > 0,
          compareRequiredModifiers: selectedItem.items_required_modifier_groups.length > 0,
          itemIndex: parseInt(currentEditItem ?? sectionSelected),
        })
      : null;

  const label =
    selectedItem?.category_id == REACT_APP_SOUP_CATEGORY_ID_FOR_TRY2COMBO_ADDONS_UPSELL && isOldAndNewBucketSame
      ? 'No Thanks'
      : 'Apply';

  return (
    <Modal
      dialogClassName="modal-sm-90w"
      show={showCustomizationModal}
      onHide={closeModal}
      centered
      className="theme-modal-style app_container_wrap no-padding border-radius-15 border-radius-sm-12 upperside-radius-sm-only other-dressings-modal try2_dressing_modal scroll_modal dressing_modal_ms_mob"
    >
      <Modal.Header className="pt-0" id="close_button2">
        <button
          type="button"
          className="btn modal-close-button pe-0 sm-blue-icon"
          onClick={closeModal}
        >
          Close
        </button>
      </Modal.Header>
      {openAccordionFor.current &&
      <Modal.Body className="app_container_wrap px-0 px-md-1 pb-2 pt-4 text-center app_container_wrap">
        {isCreateYourOwnItem ? (
          <h2 className="font-Cls f-s22 text-capitalize f-sm-s18 ls-normal lh-normal text-center py-2 ">
            {itemName}
          </h2>
        ) : (
          <h2 className="font-Cls f-s22 f-sm-s18 ls-normal lh-normal text-center py-2  text-capitalize">
            Customize This Item{' '}
          </h2>
        )}
        <div className={`${styleClasses.items_wrap} mb-4  py-0 px-md-0`}>
          <AddIngredients
            modifierGroups={modifierGroups}
            coreModifiers={coreModifiers}
            requiredModifiers={requiredModifiers}
            handleIngredientSelection={(data: IModifiersObjects) =>
              updateSelectedModifiers(data)
            }
            selectedItem={selectedItem}
            handleSyncIngredients={handleSyncIngredients}
            sectionSelected={currentEditItem}
            noDressingHandler={noDressingHandler}
            itemName={itemName}
            is_create_your_own={isCreateYourOwnItem}
            isItEdit={isItEdit}
            openAccordionFor={openAccordionFor}
          />
          <ApplyButton
            isDisabled={showProteinModalOnCombo ? false : disableApply}
            clickHandle={addSelectedIngredients}
            btnClass={`${styleClasses.large_btn} mb-sm-5`}
            label={label}
          />
        </div>
      </Modal.Body>
}
    </Modal>
  );
};
export default Try2CustomizationModal;
